import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DashboardNavbar } from '../../components/layout/DashboardNavbar';
import { PlayerProfileContent } from '../dashboard/PlayerProfileContent';
import PlayerProfileHeader from './profile/PlayerProfileHeader';
import CompleteProfileModal from './CompleteProfileModal';
import LevelUpProfileModal from './LevelUpProfileModal';
import LevelUpProfileForm from './LevelUpProfileForm';
import { getPlayerProfile } from '../../features/playerProfile/playerProfileSlice';

export const PlayerDashboard = () => {
  const dispatch = useDispatch();
  const { profile, loading } = useSelector((state) => state.playerProfile);
  const [isCompleteProfileModalOpen, setIsCompleteProfileModalOpen] = useState(false);
  const [isLevelUpModalOpen, setIsLevelUpModalOpen] = useState(false);
  const [showCompleteProfileForm, setShowCompleteProfileForm] = useState(false);

  useEffect(() => {
    // Fetch player profile on mount
    dispatch(getPlayerProfile());
  }, [dispatch]);

  useEffect(() => {
    // Only show modal if profile is incomplete and user hasn't seen it yet
    if (profile && !loading) {
      const isComplete = profile.profileCompletion?.isComplete || profile.profileCompleteness === 100;
      const hasSeenModal = localStorage.getItem('hasSeenCompleteProfileModal');

      // Clear the flag if profile is now complete
      if (isComplete && hasSeenModal) {
        localStorage.removeItem('hasSeenCompleteProfileModal');
      }

      if (!isComplete && !hasSeenModal) {
        // Open modal after 2 seconds if profile is incomplete
        const timer = setTimeout(() => {
          setIsCompleteProfileModalOpen(true);
          // Mark that user has seen the modal
          localStorage.setItem('hasSeenCompleteProfileModal', 'true');
        }, 2000);

        return () => clearTimeout(timer);
      }
    }
  }, [profile, loading]);

  // From the CompleteProfileModal - opens modal version
  const handleCompleteProfileFromModal = () => {
    setIsLevelUpModalOpen(true);
  };

  // From the dashboard button - opens inline form
  const handleCompleteProfileFromDashboard = () => {
    setShowCompleteProfileForm(true);
  };

  // "Upload video" jumps down to the video card
  const handleUploadVideo = () => {
    setShowCompleteProfileForm(false);
    setTimeout(() => document.getElementById('player-videos')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
  };

  return (
    <div className="min-h-screen bg-jp-page text-jp-ink text-left overflow-x-hidden">
      <DashboardNavbar userRole="player" />

      {/* Navy header + profile progress, laid out like the public player preview */}
      <PlayerProfileHeader
        onCompleteProfile={handleCompleteProfileFromDashboard}
        onUploadVideo={handleUploadVideo}
      />

      {/* Show Complete Profile Form or Regular Content */}
      {showCompleteProfileForm ? (
        <div className="px-4 sm:px-6 lg:px-8 py-10 sm:py-12">
          <LevelUpProfileForm onBack={() => setShowCompleteProfileForm(false)} />
        </div>
      ) : (
        <PlayerProfileContent />
      )}

      {/* Complete Profile Modal */}
      <CompleteProfileModal
        isOpen={isCompleteProfileModalOpen}
        onClose={() => setIsCompleteProfileModalOpen(false)}
        onComplete={handleCompleteProfileFromModal}
        progress={profile?.profileCompleteness || profile?.profileCompletion?.percentage || 0}
        playerName={profile?.firstName || 'Player'}
      />

      {/* Level Up Profile Modal */}
      <LevelUpProfileModal
        isOpen={isLevelUpModalOpen}
        onClose={() => setIsLevelUpModalOpen(false)}
        playerName={profile?.firstName || 'Player'}
      />
    </div>
  );
};

export default PlayerDashboard;
